// Inline profile completion for cleaners on first visit (missing name/language)
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useUpdateProfileLanguage } from "@/hooks/useProfileLanguage";
import { LanguageSelector } from "@/components/cleaner/LanguageSelector";
import { LanguageCode, getStoredLanguage } from "@/lib/languages";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

const STORAGE_KEY = "worker_profile_completion_dismissed";

export function ProfileCompletionModal() {
  const { profile, refetchProfile } = useAuth();
  const updateLanguage = useUpdateProfileLanguage();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [language, setLanguage] = useState<LanguageCode>("en");
  const [saving, setSaving] = useState(false);

  const profileLang = (profile as { language?: string })?.language;
  const currentLang: LanguageCode = profileLang || getStoredLanguage();

  const hasName = !!(profile?.first_name?.trim() || profile?.full_name?.trim());
  const hasLanguage = !!profileLang;

  useEffect(() => {
    setFirstName(profile?.first_name || "");
    setLastName(profile?.last_name || "");
    setLanguage(currentLang);
  }, [profile?.first_name, profile?.last_name, currentLang]);

  useEffect(() => {
    if (!profile || profile.role !== "cleaner") return;
    const dismissed = sessionStorage.getItem(STORAGE_KEY) === "true";
    if (!dismissed && (!hasName || !hasLanguage)) {
      setOpen(true);
    }
  }, [profile?.id, profile?.role, hasName, hasLanguage]);

  const handleSave = async () => {
    if (!profile?.id) return;
    setSaving(true);
    try {
      const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") || null;
      const { error } = await supabase
        .from("profiles")
        .update({
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          full_name: fullName,
          language,
        })
        .eq("id", profile.id);
      if (error) throw error;
      await updateLanguage.mutateAsync(language);
      await refetchProfile();
      sessionStorage.setItem(STORAGE_KEY, "true");
      setOpen(false);
      toast({ title: "Profile updated" });
    } catch (e) {
      toast({
        title: "Could not save",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleDismiss()}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Complete your profile</DialogTitle>
          <DialogDescription>
            Add your name and preferred language so we can personalize your experience.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>First name</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="space-y-2">
              <Label>Last name</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Language</Label>
            <LanguageSelector
              value={language}
              onChange={(v) => setLanguage(v)}
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="ghost" onClick={handleDismiss}>
              Skip
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
